import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { writePrintDoc } from '../../utils/printUtils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Briefcase, Printer, Download, FileSpreadsheet, FileText, Search } from 'lucide-react';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const AllEntrepreneursReport = () => {
  const [entrepreneurs, setEntrepreneurs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branchFilter, setBranchFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // new | existing
  const [sectorFilter, setSectorFilter] = useState('all'); // operation_type
  const [casteFilter, setCasteFilter] = useState('all');
  const [genderFilter, setGenderFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [eRes, bRes] = await Promise.all([
          api.get('/entrepreneurs', { params: { limit: 5000 } }),
          api.get('/branches'),
        ]);
        setEntrepreneurs(eRes.data || []);
        setBranches(bRes.data || []);
      } catch {
        toast.error('Failed to load entrepreneurs');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Distinct sectors / castes / genders present in current data — keeps UI honest
  const distinctSectors = useMemo(() => {
    const s = new Set();
    entrepreneurs.forEach(e => { const v = e.enterprise?.operation_type; if (v) s.add(v); });
    return Array.from(s).sort();
  }, [entrepreneurs]);
  const distinctCastes = useMemo(() => {
    const s = new Set();
    entrepreneurs.forEach(e => { if (e.caste) s.add(e.caste); });
    return Array.from(s).sort();
  }, [entrepreneurs]);
  const distinctGenders = useMemo(() => {
    const s = new Set();
    entrepreneurs.forEach(e => { if (e.gender) s.add(e.gender); });
    return Array.from(s).sort();
  }, [entrepreneurs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entrepreneurs.filter(e => {
      if (branchFilter !== 'all' && e.branch_id !== branchFilter) return false;
      if (statusFilter !== 'all') {
        const isExisting = !!e.enterprise?.is_existing;
        if (statusFilter === 'existing' && !isExisting) return false;
        if (statusFilter === 'new' && isExisting) return false;
      }
      if (sectorFilter !== 'all' && (e.enterprise?.operation_type || '') !== sectorFilter) return false;
      if (casteFilter !== 'all' && (e.caste || '') !== casteFilter) return false;
      if (genderFilter !== 'all' && (e.gender || '') !== genderFilter) return false;
      if (!q) return true;
      return (
        (e.name || '').toLowerCase().includes(q) ||
        (e.enrollment_no || '').toLowerCase().includes(q) ||
        (e.mobile || '').toLowerCase().includes(q) ||
        (e.enterprise?.name || '').toLowerCase().includes(q)
      );
    });
  }, [entrepreneurs, branchFilter, statusFilter, sectorFilter, casteFilter, genderFilter, search]);

  const branchName = branchFilter === 'all'
    ? 'All Branches'
    : (branches.find(b => b.id === branchFilter)?.name || '-');

  // Build the rows once for export reuse
  const buildRow = (e, i) => {
    const br = branches.find(b => b.id === e.branch_id);
    return {
      sno: i + 1,
      enrollment_no: e.enrollment_no || '',
      name: e.name || '',
      branch: br?.name || '',
      gender: e.gender || '',
      caste: e.caste || '',
      mobile: e.mobile || '',
      pan: e.pan_number || '',
      aadhaar: e.aadhaar_number || '',
      state: e.state || '',
      district: e.district || '',
      block: e.block || '',
      gram_panchayat: e.gram_panchayat || '',
      village: e.village || '',
      pincode: e.pincode || '',
      shg_vlf: e.shg_vlf_name || '',
      vo_name: e.vo_name || '',
      clf_name: e.clf_name || '',
      enterprise_name: e.enterprise?.name || '',
      enterprise_type: e.enterprise?.type || '',
      operation_type: e.enterprise?.operation_type || '',
      product_item: e.enterprise?.product_item || '',
      ownership: e.enterprise?.ownership_structure || '',
      govt_license: e.enterprise?.govt_license || '',
      loan_type: e.enterprise?.loan_type || '',
      proposed_loan: e.enterprise?.proposed_loan_amount || 0,
      is_existing: e.enterprise?.is_existing ? 'Existing' : 'New',
    };
  };

  const handlePrint = () => {
    const rows = filtered.map((e, i) => {
      const r = buildRow(e, i);
      return `<tr style="background:${i % 2 ? '#f9fafb' : '#fff'}">
        <td>${r.sno}</td><td>${r.enrollment_no}</td><td>${r.name}</td><td>${r.branch}</td>
        <td>${r.gender}</td><td>${r.caste}</td><td>${r.mobile}</td>
        <td>${r.enterprise_name}</td><td>${r.enterprise_type}</td><td>${r.operation_type}</td>
        <td>${r.loan_type}</td><td>${r.is_existing}</td>
      </tr>`;
    }).join('');
    const html = `<!doctype html><html><head><title>All Entrepreneurs — WWAGS MIS</title>
    <style>
      body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}
      h1{color:#047857;font-size:20px;margin:0 0 4px}
      .meta{color:#64748b;font-size:12px;margin-bottom:14px}
      table{width:100%;border-collapse:collapse;font-size:10.5px}
      th{background:#047857;color:#fff;padding:8px;text-align:left}
      td{padding:6px 8px;border-bottom:1px solid #e2e8f0}
      @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}@page{size:A4 landscape;margin:12mm}}
    </style></head><body>
      <h1>WWAGS MIS — All Entrepreneurs</h1>
      <p class="meta">Branch: <b>${branchName}</b> &nbsp;•&nbsp; Total: <b>${filtered.length}</b> &nbsp;•&nbsp; Generated: ${new Date().toLocaleString('en-IN')}</p>
      <table>
        <thead><tr>
          <th>#</th><th>Enrollment</th><th>Name</th><th>Branch</th>
          <th>Gender</th><th>Caste</th><th>Mobile</th>
          <th>Enterprise</th><th>Type</th><th>Operation</th>
          <th>Loan</th><th>Status</th>
        </tr></thead>
        <tbody>${rows || '<tr><td colspan="12" style="text-align:center;color:#94a3b8;padding:20px">No entrepreneurs</td></tr>'}</tbody>
      </table>
    </body></html>`;
    const w = window.open('', '_blank');
    if (w) { writePrintDoc(w.document, html); setTimeout(() => w.print(), 500); }
  };

  const handleExportExcel = () => {
    const rows = filtered.map(buildRow);
    if (rows.length === 0) { toast.error('No entrepreneurs to export'); return; }
    const ws = XLSX.utils.json_to_sheet(rows.map(r => ({
      'S.No.': r.sno,
      'Enrollment No.': r.enrollment_no,
      'Name': r.name,
      'Branch': r.branch,
      'Gender': r.gender,
      'Caste': r.caste,
      'Mobile': r.mobile,
      'PAN': r.pan,
      'Aadhaar': r.aadhaar,
      'District': r.district,
      'State': r.state,
      'SHG Name': r.shg_vlf,
      'VO Name': r.vo_name,
      'CLF Name': r.clf_name,
      'Enterprise': r.enterprise_name,
      'Sector': r.enterprise_type,
      'Operation Type': r.operation_type,
      'Loan Type': r.loan_type,
      'Proposed Loan (Rs.)': r.proposed_loan,
      'Status': r.is_existing,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Entrepreneurs');
    const fname = `WWAGS_All_Entrepreneurs_${branchName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fname);
    toast.success(`Exported ${rows.length} entrepreneurs to Excel`);
  };

  const handleExportPDF = () => {
    if (filtered.length === 0) { toast.error('No entrepreneurs to export'); return; }
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt' });
    doc.setFontSize(14);
    doc.setTextColor(4, 120, 87);
    doc.text('WWAGS MIS — All Entrepreneurs', 40, 36);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Branch: ${branchName}   •   Total: ${filtered.length}   •   Generated: ${new Date().toLocaleString('en-IN')}`, 40, 52);

    const head = [['#', 'Enrollment', 'Name', 'Branch', 'Gender', 'Caste', 'Mobile', 'Enterprise', 'Sector', 'Operation', 'Loan', 'Status']];
    const body = filtered.map((e, i) => {
      const r = buildRow(e, i);
      return [r.sno, r.enrollment_no, r.name, r.branch, r.gender, r.caste, r.mobile, r.enterprise_name, r.enterprise_type, r.operation_type, r.loan_type, r.is_existing];
    });
    autoTable(doc, {
      startY: 64,
      head,
      body,
      styles: { fontSize: 7.5, cellPadding: 3 },
      headStyles: { fillColor: [4, 120, 87], textColor: 255 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    const fname = `WWAGS_All_Entrepreneurs_${branchName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fname);
    toast.success(`Exported ${filtered.length} entrepreneurs to PDF`);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="all-entrepreneurs-report-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/reports" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-emerald-700" /> All Entrepreneurs Report
          </h1>
          <p className="text-sm text-slate-600">Branch-filterable list of every entrepreneur with print, Excel and PDF export.</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <Label className="text-xs">Search</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name / enrollment / mobile / enterprise" className="pl-8" data-testid="ae-search" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Branch</Label>
          <Select value={branchFilter} onValueChange={setBranchFilter}>
            <SelectTrigger data-testid="ae-branch-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <Button variant="outline" onClick={handlePrint} className="flex-1" data-testid="ae-print"><Printer className="w-4 h-4 mr-1" /> Print</Button>
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="ae-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All (New + Existing)</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="existing">Existing</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Sector (Operation)</Label>
          <Select value={sectorFilter} onValueChange={setSectorFilter}>
            <SelectTrigger data-testid="ae-sector-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sectors</SelectItem>
              {distinctSectors.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Caste</Label>
          <Select value={casteFilter} onValueChange={setCasteFilter}>
            <SelectTrigger data-testid="ae-caste-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Castes</SelectItem>
              {distinctCastes.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Gender</Label>
          <Select value={genderFilter} onValueChange={setGenderFilter}>
            <SelectTrigger data-testid="ae-gender-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Genders</SelectItem>
              {distinctGenders.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap items-center gap-3 justify-between">
        <div className="text-sm flex flex-wrap items-center gap-2">
          <span className="text-slate-500">Showing</span> <b>{filtered.length}</b>
          <span className="text-slate-500">of {entrepreneurs.length} entrepreneurs</span>
          <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">{branchName}</span>
          {statusFilter !== 'all' && <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full capitalize">{statusFilter}</span>}
          {sectorFilter !== 'all' && <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">{sectorFilter}</span>}
          {casteFilter !== 'all' && <span className="text-xs px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full">{casteFilter}</span>}
          {genderFilter !== 'all' && <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">{genderFilter}</span>}
          {(statusFilter !== 'all' || sectorFilter !== 'all' || casteFilter !== 'all' || genderFilter !== 'all') && (
            <button
              type="button"
              onClick={() => { setStatusFilter('all'); setSectorFilter('all'); setCasteFilter('all'); setGenderFilter('all'); }}
              className="text-xs underline text-slate-500 hover:text-slate-700"
              data-testid="ae-clear-filters"
            >clear filters</button>
          )}
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExportExcel} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="ae-excel">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
          <Button onClick={handleExportPDF} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="ae-pdf">
            <FileText className="w-4 h-4 mr-1" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-700"></div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-emerald-700 text-white sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-left">#</th>
                  <th className="px-2 py-2 text-left">Enrollment</th>
                  <th className="px-2 py-2 text-left">Name</th>
                  <th className="px-2 py-2 text-left">Branch</th>
                  <th className="px-2 py-2 text-left">Gender</th>
                  <th className="px-2 py-2 text-left">Caste</th>
                  <th className="px-2 py-2 text-left">Mobile</th>
                  <th className="px-2 py-2 text-left">Enterprise</th>
                  <th className="px-2 py-2 text-left">Sector</th>
                  <th className="px-2 py-2 text-left">Operation</th>
                  <th className="px-2 py-2 text-left">Loan</th>
                  <th className="px-2 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.length === 0 ? (
                  <tr><td colSpan={12} className="px-3 py-10 text-center text-slate-400">No entrepreneurs match your filter.</td></tr>
                ) : (
                  filtered.map((e, i) => {
                    const r = buildRow(e, i);
                    return (
                      <tr key={e.id} className={i % 2 ? 'bg-slate-50' : ''} data-testid={`ae-row-${e.id}`}>
                        <td className="px-2 py-1.5">{r.sno}</td>
                        <td className="px-2 py-1.5 font-mono text-[10px]">{r.enrollment_no}</td>
                        <td className="px-2 py-1.5 font-medium">{r.name}</td>
                        <td className="px-2 py-1.5">{r.branch}</td>
                        <td className="px-2 py-1.5">{r.gender}</td>
                        <td className="px-2 py-1.5">{r.caste}</td>
                        <td className="px-2 py-1.5">{r.mobile}</td>
                        <td className="px-2 py-1.5">{r.enterprise_name}</td>
                        <td className="px-2 py-1.5">{r.enterprise_type}</td>
                        <td className="px-2 py-1.5">{r.operation_type}</td>
                        <td className="px-2 py-1.5"><span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">{r.loan_type}</span></td>
                        <td className="px-2 py-1.5">{r.is_existing}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllEntrepreneursReport;
